import { api } from './api';
import type { CalEvent, EventKind, EventTone } from './diaryStore';

interface ApiCalendarEvent {
  id: string;
  ownerId: string;
  caseId: string | null;
  hearingId: string | null;
  title: string;
  description: string | null;
  startAt: string;
  endAt: string;
  allDay: boolean;
  location: string | null;
  videoUrl: string | null;
  eventType: string;
  status: string;
  createdAt: string;
  owner: { id: string; firstName: string; lastName: string; memberType: string } | null;
  case: {
    id: string;
    caseNumber: string;
    caseName: string;
    status: string;
    priority: string;
    opponentParty: string | null;
    opponentCounsel: string | null;
    court: { name: string; shortName: string } | null;
    layClient: { firstName: string | null; lastName: string | null; company: string | null } | null;
  } | null;
  hearing: {
    id: string;
    hearingType: string;
    status: string;
    judgeName: string | null;
    courtroom: string | null;
    court: { name: string; shortName: string } | null;
  } | null;
  // RC-STAGE3 card enrichment (batch readers on /calendar) — no UI recompute.
  readiness: { percentage: number; status: string; missingCount: number } | null;
  obligationsBlocked: number;
  lifecycle: string;
}

/** Prefer a company/organisation name, else the person's name. */
function contactName(
  c: { firstName: string | null; lastName: string | null; company: string | null } | null | undefined,
): string | undefined {
  if (!c) return undefined;
  if (c.company) return c.company;
  const n = [c.firstName, c.lastName].filter(Boolean).join(' ').trim();
  return n || undefined;
}

/** "pre_trial_review" → "pre trial review" (display-only; a proper label map can come later). */
function humanize(s: string | null | undefined): string | undefined {
  return s ? s.replace(/_/g, ' ') : undefined;
}

function typeToKind(t: string): EventKind {
  const map: Record<string, EventKind> = {
    hearing: 'hearing', consultation: 'conference', conference: 'conference',
    meeting: 'meeting', travel: 'travel', out_of_office: 'leave',
    cpd_training: 'personal', personal: 'personal', other: 'task',
  };
  return map[t] ?? 'task';
}

const KIND_TONE: Record<EventKind, EventTone> = {
  hearing: 'brand', conference: 'amber', task: 'violet', brief_return: 'red', personal: 'emerald',
  meeting: 'violet', travel: 'emerald', leave: 'red',
};

function apiToCalEvent(e: ApiCalendarEvent): CalEvent {
  const start = new Date(e.startAt);
  const end = new Date(e.endAt);
  // The app stores wall-clock time AS UTC (`${date}T${time}:00Z`), so read it back in UTC —
  // exactly like hearingsApi (`toISOString().slice(11,16)`) and the matter/hearings page.
  // Browser-local getHours() shifted every event by the viewer's TZ offset (e.g. +5:30 in
  // IST), which is why the diary showed a different time than the matter page (and late
  // events could disappear past the grid or roll onto the next day).
  const startHour = Math.max(0, start.getUTCHours() - 9); // 09:00 = slot 0
  const spanHours = Math.max(1, Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60)));
  const kind = typeToKind(e.eventType);
  const ownerName = e.owner ? `${e.owner.firstName} ${e.owner.lastName}` : '';
  const suffix = e.owner?.memberType === 'silk' ? ' KC' : '';

  return {
    id: e.id,
    date: e.startAt.slice(0, 10),        // UTC date — canonical, matches the matter page
    start: startHour,
    startTime: e.startAt.slice(11, 16),  // UTC "HH:MM" — the real hearing time, not a 09:00 default
    span: spanHours,
    title: e.title,
    sub: e.location ?? e.case?.caseName ?? '',
    location: e.location ?? undefined,
    kind,
    tone: KIND_TONE[kind],
    caseId: e.caseId ?? undefined,
    hearingId: e.hearingId ?? undefined, // link back to the hearing so a diary edit updates the SOURCE
    notes: e.description ?? undefined,
    ownerId: e.ownerId,
    barrister: ownerName + suffix,
    createdBy: ownerName,
    status: e.status as CalEvent['status'],
    matter: e.case
      ? {
          number: e.case.caseNumber,
          name: e.case.caseName,
          status: humanize(e.case.status),
          priority: e.case.priority,
          client: contactName(e.case.layClient),
          court:
            e.hearing?.court?.shortName ??
            e.hearing?.court?.name ??
            e.case.court?.shortName ??
            e.case.court?.name ??
            undefined,
          opponent: e.case.opponentParty ?? e.case.opponentCounsel ?? undefined,
        }
      : undefined,
    hearingType: humanize(e.hearing?.hearingType),
    hearingStatus: e.hearing?.status ?? undefined,
    judge: e.hearing?.judgeName ?? undefined,
    courtroom: e.hearing?.courtroom ?? undefined,
    readinessPct: e.readiness?.percentage,
    obligationsBlocked: e.obligationsBlocked,
    lifecycle: e.lifecycle,
  };
}

export interface CreateCalendarEventPayload {
  title: string;
  startAt: string;
  endAt: string;
  eventType?: string;
  description?: string;
  location?: string;
  videoUrl?: string;
  caseId?: string;
  allDay?: boolean;
  status?: string;
  /** Pass the barrister's User UUID when a clerk creates an event on their behalf.
   *  The event is stored under the barrister's ownership so it appears in their diary. */
  forUserId?: string;
  /** Clerk(s) explicitly asked to coordinate this event ("Requires clerk coordination").
   *  The server validates each against the barrister's eligible supporting clerks. */
  coordinationClerkIds?: string[];
}

export async function fetchCalendarEvents(options?: { ownerId?: string; caseId?: string }): Promise<CalEvent[]> {
  // Fetch events from 30 days ago to 12 months ahead — prevents past events
  // consuming the limit and truncating upcoming hearings as chambers history grows.
  const from = new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString();
  const to = new Date(Date.now() + 365 * 24 * 3600 * 1000).toISOString();
  const res = await api.get('/calendar', {
    params: {
      limit: 200,
      sortBy: 'startAt',
      sortOrder: 'asc',
      from,
      to,
      ...(options?.ownerId && { ownerId: options.ownerId }),
      ...(options?.caseId && { caseId: options.caseId }),
    },
  });
  const payload = res.data?.data ?? res.data;
  const events: ApiCalendarEvent[] = payload.events ?? [];
  return events.map(apiToCalEvent);
}

/** An opaque availability slot — time only, never any event detail. */
export interface FreeBusySlot {
  start: string;
  end: string;
  status: 'busy' | 'tentative';
}

/**
 * Opaque free/busy for a barrister: when are they occupied? Returns start/end/status only — never
 * titles/notes/matters (the confidentiality boundary is enforced server-side). Used to show a clerk
 * a barrister's "Busy" blocks without exposing private event content.
 */
export async function fetchBarristerFreeBusy(barristerId: string, from: string, to: string): Promise<FreeBusySlot[]> {
  const res = await api.get<{ data: FreeBusySlot[] }>('/calendar/free-busy', {
    params: { barristerId, from, to },
  });
  return res.data.data;
}

export async function createCalendarEvent(payload: CreateCalendarEventPayload): Promise<CalEvent> {
  const res = await api.post('/calendar', payload);
  const event = (res.data as any).data ?? res.data;
  return apiToCalEvent(event as ApiCalendarEvent);
}

export async function updateCalendarEvent(
  id: string,
  // startAt/endAt included: the backend (calendar.service.updateCalendarEvent) already
  // persists them; omitting them here silently dropped every time/date edit of a
  // self-owned diary event (personal/meeting/travel/consultation).
  patch: Partial<Pick<CreateCalendarEventPayload, 'title' | 'description' | 'location' | 'videoUrl' | 'status' | 'startAt' | 'endAt'>>,
): Promise<void> {
  await api.patch(`/calendar/${id}`, patch);
}

export async function deleteCalendarEvent(id: string): Promise<void> {
  await api.delete(`/calendar/${id}`);
}

// ─── Raw schedule fetch (precise ISO times for conflict detection) ────────────

export interface RawScheduleEvent {
  id: string;
  startAt: string;
  endAt: string;
  title: string;
  eventType: string;
  case: { id: string; caseNumber: string; caseName: string } | null;
  location: string | null;
}

export async function fetchBarristerScheduleRaw(
  barristerId: string,
  from: string,
  to: string,
): Promise<RawScheduleEvent[]> {
  const res = await api.get('/calendar', {
    params: { limit: 100, sortBy: 'startAt', sortOrder: 'asc', ownerId: barristerId, from, to },
  });
  const payload = res.data?.data ?? res.data;
  const events: ApiCalendarEvent[] = payload.events ?? [];
  return events.map((e) => ({
    id: e.id,
    startAt: e.startAt,
    endAt: e.endAt,
    title: e.title,
    eventType: e.eventType,
    case: e.case,
    location: e.location,
  }));
}
